import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { useRef } from 'react';
import {
  ArrowLeft,
  Camera,
  Check,
  EyeOff,
  Globe,
  Lock,
  Loader2,
  Pencil,
  Trash2,
  User,
  UserX,
  X,
} from 'lucide-react';
import { SkeletonRow } from './Skeleton';
import EmptyState from './EmptyState';
import { api } from '../lib/api';
import './DraftsPanel.css';

export type ThreadVisibility = 'private' | 'public' | 'anonymous' | null;

export type ThreadListItem = {
  id: number;
  title: string;
  questionCount: number;
  createdAt: string;
  lastActivityAt: string | null;
  status: 'open' | 'published' | 'archived';
  visibility: ThreadVisibility;
  hasReport: boolean;
  publishedAt: string | null;
};

export type ProfileSummary = {
  user: {
    id: number;
    email: string;
    createdAt: string;
    displayName: string | null;
    bio: string | null;
    avatarUrl: string | null;
  };
  stats: {
    draftCount: number;
    publishedCount: number;
    totalInsights: number;
    totalSources: number;
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  onPick: (threadId: number) => void;
  onSignOut?: () => void;
  maxQuestions?: number;
};

type TabKind = 'drafts' | 'published';
type DrawerView = 'list' | 'profile' | 'sources';

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return '1 week ago';
  if (weeks < 5) return `${weeks} weeks ago`;
  return new Date(iso).toLocaleDateString();
}

function VisibilityBadge({ visibility }: { visibility: ThreadVisibility }) {
  if (visibility === 'public') {
    return (
      <span className="work__badge work__badge--public" aria-label="public">
        <Globe size={11} /> public
      </span>
    );
  }
  if (visibility === 'anonymous') {
    return (
      <span className="work__badge work__badge--anon" aria-label="anonymous">
        <UserX size={11} /> anon
      </span>
    );
  }
  if (visibility === 'private') {
    return (
      <span className="work__badge work__badge--private" aria-label="private">
        <Lock size={11} /> private
      </span>
    );
  }
  return (
    <span className="work__badge work__badge--draft" aria-label="draft">
      <EyeOff size={11} /> draft
    </span>
  );
}

/* ── Resize image to a square JPEG data URL ────────────────────────── */
function resizeImage(file: File, maxPx: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const size = Math.min(img.width, img.height, maxPx);
      const sx = (img.width - Math.min(img.width, img.height)) / 2;
      const sy = (img.height - Math.min(img.width, img.height)) / 2;
      const sLen = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, sx, sy, sLen, sLen, 0, 0, size, size);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });
}


/* ── Sources list ──────────────────────────────────────────────────── */
type SourceItem = {
  url: string;
  title: string;
  domain: string;
  threadTitle: string;
  threadId: number;
};

function SourcesList({ onBack }: { onBack: () => void }) {
  const [sources, setSources] = useState<SourceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/profile/sources', { credentials: 'include' });
        const data = (await res.json()) as {
          ok?: boolean;
          sources?: SourceItem[];
          error?: string;
        };
        if (!res.ok || !data.ok) throw new Error(data.error ?? 'Failed to load');
        setSources(data.sources ?? []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load sources');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <header className="drafts-drawer__head">
        <button className="drafts-drawer__back" onClick={onBack} aria-label="Back">
          <ArrowLeft size={16} />
        </button>
        <span className="drafts-drawer__title">SOURCES</span>
        <span className="drafts-drawer__count">{sources.length}</span>
      </header>

      <div className="drafts-drawer__body">
        {loading && (
          <div className="sources__state">
            <Loader2 size={14} className="drafts-drawer__spin" /> loading...
          </div>
        )}
        {error && <div className="drafts-drawer__error">{error}</div>}
        {!loading && !error && sources.length === 0 && (
          <EmptyState
            title="no sources collected yet."
            subtitle="sources appear as you ask questions."
          />
        )}
        {!loading && !error && sources.length > 0 && (
          <ul className="sources__list">
            {sources.map((s, i) => (
              <li key={`${s.url}-${i}`}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sources__item"
                >
                  <span className="sources__domain">{s.domain}</span>
                  <span className="sources__title">{s.title}</span>
                  <span className="sources__thread">from: {s.threadTitle}</span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

/* ── Profile edit form ──────────────────────────────────────────────── */
function ProfileEdit({
  profile,
  onBack,
  onSaved,
}: {
  profile: ProfileSummary;
  onBack: () => void;
  onSaved: (p: ProfileSummary) => void;
}) {
  const [displayName, setDisplayName] = useState(profile.user.displayName ?? '');
  const [bio, setBio] = useState(profile.user.bio ?? '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(profile.user.avatarUrl);
  const [avatarDirty, setAvatarDirty] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleAvatarPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeImage(file, 256);
      setAvatarPreview(dataUrl);
      setAvatarDirty(true);
    } catch {
      setError('Could not process image');
    }
  };

  const handleAvatarRemove = () => {
    setAvatarPreview(null);
    setAvatarDirty(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const requests: Promise<Response>[] = [
        fetch('/api/profile', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            displayName,
            bio,
          }),
        }),
      ];
      if (avatarDirty) {
        requests.push(
          fetch('/api/profile/avatar', {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ avatarUrl: avatarPreview }),
          })
        );
      }
      const responses = await Promise.all(requests);
      for (const res of responses) {
        const data = await res.json() as { ok?: boolean; error?: string };
        if (!res.ok || !data.ok) throw new Error(data.error ?? 'Save failed');
      }
      onSaved({
        ...profile,
        user: {
          ...profile.user,
          displayName: displayName || null,
          bio: bio || null,
          avatarUrl: avatarPreview,
        },
      });
      setAvatarDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="drafts-drawer__head">
        <button className="drafts-drawer__back" onClick={onBack} aria-label="Back">
          <ArrowLeft size={16} />
        </button>
        <span className="drafts-drawer__title">EDIT PROFILE</span>
        <button
          className="drafts-drawer__save"
          onClick={handleSave}
          disabled={saving}
          aria-label="Save profile"
        >
          {saving ? (
            <Loader2 size={14} className="drafts-drawer__spin" />
          ) : saved ? (
            <Check size={14} />
          ) : (
            'save'
          )}
        </button>
      </header>

      <div className="drafts-drawer__body">
        {error && <div className="drafts-drawer__error">{error}</div>}

        <div className="profile-edit">
          <div className="profile-edit__avatar-section">
            <div
              className="profile-edit__avatar"
              onClick={() => fileInputRef.current?.click()}
            >
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt="Avatar"
                  className="profile-edit__avatar-img"
                />
              ) : (
                <User size={24} className="profile-edit__avatar-placeholder" />
              )}
              <div className="profile-edit__avatar-overlay">
                <Camera size={14} />
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="profile-edit__avatar-input"
              onChange={handleAvatarPick}
            />
            {avatarPreview && (
              <button
                className="profile-edit__avatar-remove"
                onClick={handleAvatarRemove}
                type="button"
              >
                <Trash2 size={12} />
                <span>remove</span>
              </button>
            )}
          </div>

          <label className="profile-edit__label">
            <span className="profile-edit__label-text">display name</span>
            <input
              className="profile-edit__input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="your name"
              maxLength={80}
            />
          </label>

          <label className="profile-edit__label">
            <span className="profile-edit__label-text">bio</span>
            <textarea
              className="profile-edit__textarea"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="a short bio about yourself"
              maxLength={280}
              rows={3}
            />
            <span className="profile-edit__char-count">{bio.length}/280</span>
          </label>
        </div>
      </div>
    </>
  );
}

export default function DraftsPanel({
  open,
  onClose,
  onPick,
  onSignOut,
  maxQuestions = 20,
}: Props) {
  const [view, setView] = useState<DrawerView>('list');
  const [tab, setTab] = useState<TabKind>('drafts');
  const [drafts, setDrafts] = useState<ThreadListItem[] | null>(null);
  const [published, setPublished] = useState<ThreadListItem[] | null>(null);
  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileRes, draftsRes, pubRes] = await Promise.all([
        fetch('/api/profile', { credentials: 'include' }),
        fetch('/api/threads?status=open', { credentials: 'include' }),
        fetch('/api/threads?status=published', { credentials: 'include' }),
      ]);
      const [profileData, draftsData, pubData] = await Promise.all([
        profileRes.json() as Promise<{
          ok?: boolean;
          user?: ProfileSummary['user'];
          stats?: ProfileSummary['stats'];
          error?: string;
        }>,
        draftsRes.json() as Promise<{
          ok?: boolean;
          threads?: ThreadListItem[];
          error?: string;
        }>,
        pubRes.json() as Promise<{
          ok?: boolean;
          threads?: ThreadListItem[];
          error?: string;
        }>,
      ]);

      if (!profileRes.ok || !profileData.ok || !profileData.user) {
        throw new Error(profileData.error ?? 'Failed to load profile');
      }
      if (!draftsRes.ok || !draftsData.ok) {
        throw new Error(draftsData.error ?? 'Failed to load drafts');
      }
      if (!pubRes.ok || !pubData.ok) {
        throw new Error(pubData.error ?? 'Failed to load published');
      }

      setProfile({
        user: profileData.user,
        stats: profileData.stats ?? {
          draftCount: 0,
          publishedCount: 0,
          totalInsights: 0,
          totalSources: 0,
        },
      });
      setDrafts(draftsData.threads ?? []);
      setPublished(pubData.threads ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load your work');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void load();
    } else {
      setView('list');
    }
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const list = tab === 'drafts' ? drafts : published;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="drafts-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.aside
            className="drafts-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Your work"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 34 }}
          >
            {view === 'sources' ? (
              <SourcesList onBack={() => setView('list')} />
            ) : view === 'profile' && profile ? (
              <ProfileEdit
                profile={profile}
                onBack={() => setView('list')}
                onSaved={(p) => setProfile(p)}
              />
            ) : (
              <>
                <header className="drafts-drawer__head">
                  <div className="drafts-drawer__title-row">
                    <User size={14} className="drafts-drawer__icon" />
                    <span className="drafts-drawer__title">YOUR WORK</span>
                  </div>
                  <div className="drafts-drawer__head-actions">
                    <button
                      className="drafts-drawer__close"
                      onClick={onClose}
                      aria-label="Close drawer"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </header>

                {/* ── Profile card ─────────────────────────────────── */}
                {profile && (
                  <section className="work__profile">
                    <div className="work__profile-top">
                      <div className="work__profile-info">
                        {profile.user.avatarUrl ? (
                          <img
                            src={profile.user.avatarUrl}
                            alt=""
                            className="work__profile-avatar"
                          />
                        ) : (
                          <div className="work__profile-avatar work__profile-avatar--empty">
                            <User size={16} />
                          </div>
                        )}
                        <div>
                          {profile.user.displayName ? (
                            <div className="work__profile-name">
                              {profile.user.displayName}
                            </div>
                          ) : (
                            <button
                              className="work__profile-name-prompt"
                              onClick={() => setView('profile')}
                            >
                              set your name
                            </button>
                          )}
                          <div className="work__profile-email">
                            {profile.user.email ?? 'device account'}
                          </div>
                          {!profile.user.email && (
                            <div className="work__profile-device-hint">
                              add an email to sync across devices
                            </div>
                          )}
                          <div className="work__profile-meta">
                            joined {relativeTime(profile.user.createdAt)}
                          </div>
                        </div>
                      </div>
                      <button
                        className="work__profile-edit"
                        onClick={() => setView('profile')}
                        aria-label="Edit profile"
                      >
                        <Pencil size={13} />
                      </button>
                    </div>
                    {profile.user.bio && (
                      <div className="work__profile-bio">{profile.user.bio}</div>
                    )}
                    <div className="work__profile-stats">
                      <div className="work__stat">
                        <span className="work__stat-value">
                          {profile.stats.draftCount}
                        </span>
                        <span className="work__stat-label">drafts</span>
                      </div>
                      <div className="work__stat">
                        <span className="work__stat-value">
                          {profile.stats.publishedCount}
                        </span>
                        <span className="work__stat-label">published</span>
                      </div>
                      <div className="work__stat">
                        <span className="work__stat-value">
                          {profile.stats.totalInsights}
                        </span>
                        <span className="work__stat-label">insights</span>
                      </div>
                      <button
                        className="work__stat work__stat--clickable"
                        onClick={() => setView('sources')}
                      >
                        <span className="work__stat-value">
                          {profile.stats.totalSources}
                        </span>
                        <span className="work__stat-label">sources</span>
                      </button>
                    </div>
                  </section>
                )}

                {/* ── Tabs ───────────────────────────────────────── */}
                <div className="work__tabs" role="tablist">
                  <button
                    role="tab"
                    aria-selected={tab === 'drafts'}
                    className={`work__tab ${
                      tab === 'drafts' ? 'work__tab--active' : ''
                    }`}
                    onClick={() => setTab('drafts')}
                  >
                    drafts
                    {profile && (
                      <span className="work__tab-count">
                        {profile.stats.draftCount}
                      </span>
                    )}
                  </button>
                  <button
                    role="tab"
                    aria-selected={tab === 'published'}
                    className={`work__tab ${
                      tab === 'published' ? 'work__tab--active' : ''
                    }`}
                    onClick={() => setTab('published')}
                  >
                    published
                    {profile && (
                      <span className="work__tab-count">
                        {profile.stats.publishedCount}
                      </span>
                    )}
                  </button>
                </div>

                {/* ── List ───────────────────────────────────────── */}
                <div className="drafts-drawer__body">
                  {loading && (
                    <>
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                    </>
                  )}
                  {error && (
                    <div className="drafts-drawer__error">
                      {error}
                      <button type="button" className="drafts-drawer__retry" onClick={load}>
                        try again
                      </button>
                    </div>
                  )}
                  {!loading && !error && list !== null && list.length === 0 && (
                    tab === 'drafts' ? (
                      <EmptyState
                        title="no research threads yet."
                        subtitle="Ask a question to start your first research thread. It will appear here as a draft until you publish it."
                      />
                    ) : (
                      <EmptyState
                        title="nothing published yet."
                        subtitle="Publish a research thread to generate a report and share your findings."
                      />
                    )
                  )}
                  {!loading && !error && list && list.length > 0 && (
                    <ul className="drafts-drawer__list">
                      {list.map((t) => {
                        const nearCap = t.questionCount >= maxQuestions - 2;
                        return (
                          <li key={t.id}>
                            <button
                              className="drafts-drawer__item"
                              onClick={() => {
                                onPick(t.id);
                                onClose();
                              }}
                            >
                              <span className="drafts-drawer__item-title">
                                {t.title}
                              </span>
                              <span className="drafts-drawer__item-meta">
                                {tab === 'drafts' ? (
                                  <span
                                    className={
                                      nearCap
                                        ? 'drafts-drawer__item-count drafts-drawer__item-count--warn'
                                        : 'drafts-drawer__item-count'
                                    }
                                  >
                                    depth {t.questionCount}/{maxQuestions}
                                  </span>
                                ) : (
                                  <VisibilityBadge
                                    visibility={t.visibility}
                                  />
                                )}
                                <span className="drafts-drawer__item-dot">
                                  ·
                                </span>
                                <span>
                                  {tab === 'drafts'
                                    ? relativeTime(
                                        t.lastActivityAt ?? t.createdAt
                                      )
                                    : relativeTime(
                                        t.publishedAt ?? t.createdAt
                                      )}
                                </span>
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {/* ── Footer ─────────────────────────────────────── */}
                <footer className="drafts-drawer__foot">
                  {onSignOut && (
                    <button
                      className="work__signout"
                      onClick={() => {
                        onSignOut();
                        onClose();
                      }}
                    >
                      sign out
                    </button>
                  )}
                </footer>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
